import React from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import {
  compile,
  createBreadcrumbWithI18n,
} from '~/apps/ninesegsAnalyzer/routes/paths'
import {
  PageContainer,
  type PageContainerProps,
} from '~/components/elements/PageContainer'
import { SurveySettingsForm } from '~/components/features/SurveySettingsForm'
import { useInvalidateCommonQuery } from '~/hooks/useCommonQuery'
import { useLogEvent } from '~/hooks/useLogEvent'
import { useNotify } from '~/hooks/useNotify'
import { exists } from '~/utils'

import { useBrandAccountId } from '../hooks/useBrandAccountId'
import { useCreateSurveyMutation } from '../hooks/useCreateSurveyMutation'
import { pathKey } from '../utils'

export const CreateSurvey = () => {
  const brandAccountId = useBrandAccountId()

  const { t } = useTranslation()
  const createBreadcrumb = React.useMemo(() => createBreadcrumbWithI18n(t), [t])
  const breadcrumbs = React.useMemo<PageContainerProps['breadcrumbs']>(
    () => [
      createBreadcrumb('top', {}),
      createBreadcrumb(pathKey, { brandAccountId }),
    ],
    [brandAccountId, createBreadcrumb],
  )

  const notify = useNotify()
  const logEvent = useLogEvent()
  const invalidateCommonQuery = useInvalidateCommonQuery()
  const navigate = useNavigate()
  const createSurveyMutation = useCreateSurveyMutation({
    onSuccess: (data) => {
      notify({ content: t('survey_created'), style: 'success' })
      logEvent('Save general survey information')

      invalidateCommonQuery()

      const surveyId = data.createSurvey?.id
      if (exists(surveyId)) {
        navigate(compile('dataSettings', { surveyId }))
      }
    },
  })

  return (
    <PageContainer
      breadcrumbs={breadcrumbs}
      executing={createSurveyMutation.isPending}
    >
      <SurveySettingsForm onSave={createSurveyMutation.mutate} />
    </PageContainer>
  )
}
